"use client";

import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// ============================================================================
// ERROR BOUNDARY COMPONENT
// ============================================================================

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private resetTimeoutId: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    // Reset error boundary when resetKeys change
    if (hasError && resetOnPropsChange && resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (key, index) => key !== prevProps.resetKeys?.[index]
      );

      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return <DefaultErrorFallback error={this.state.error} onReset={this.resetErrorBoundary} />;
    }

    return this.props.children;
  }
}

// ============================================================================
// ERROR FALLBACK COMPONENTS
// ============================================================================

interface DefaultErrorFallbackProps {
  error?: Error;
  onReset: () => void;
}

export function DefaultErrorFallback({ error, onReset }: DefaultErrorFallbackProps) {
  return (
    <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/40">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-red-800 dark:text-red-200">
          <AlertTriangle className="h-5 w-5" />
          Something went wrong
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-red-700 dark:text-red-300 text-sm">
          An unexpected error occurred. Please try refreshing the page.
        </p>

        {process.env.NODE_ENV === "development" && error && (
          <details className="text-xs">
            <summary className="cursor-pointer text-red-600 dark:text-red-400 font-medium">
              Error Details (Development Only)
            </summary>
            <pre className="mt-2 p-2 bg-red-100 dark:bg-red-900/40 rounded text-red-800 dark:text-red-200 overflow-auto">
              {error.message}
              {error.stack && `\n\nStack Trace:\n${error.stack}`}
            </pre>
          </details>
        )}

        <div className="flex gap-2">
          <Button
            onClick={onReset}
            variant="outline"
            size="sm"
            className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/40"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MOBILE-SPECIFIC ERROR FALLBACKS
// ============================================================================

export function MobileErrorFallback({ error, onReset }: DefaultErrorFallbackProps) {
  return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-950/40 dark:border-red-800">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-red-800 dark:text-red-200 font-medium text-sm mb-1">Unable to load content</h3>
          <p className="text-red-700 dark:text-red-300 text-xs mb-3">
            Something went wrong while loading this section.
          </p>

          {process.env.NODE_ENV === "development" && error && (
            <details className="mb-3">
              <summary className="cursor-pointer text-red-600 dark:text-red-400 text-xs font-medium">
                Error Details
              </summary>
              <pre className="mt-1 p-2 bg-red-100 dark:bg-red-900/40 rounded text-red-800 dark:text-red-200 text-xs overflow-auto">
                {error.message}
              </pre>
            </details>
          )}

          <Button
            onClick={onReset}
            variant="outline"
            size="sm"
            className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/40 h-8 text-xs"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Retry
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SPECIALIZED ERROR BOUNDARIES
// ============================================================================

interface SimpleTableErrorBoundaryProps {
  children: ReactNode;
  entityName?: string;
}

export function SimpleTableErrorBoundary({ children, entityName }: SimpleTableErrorBoundaryProps) {
  return (
    <ErrorBoundary
      fallback={
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/40">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400 mx-auto mb-3" />
            <h3 className="text-red-800 dark:text-red-200 font-medium mb-2">Failed to load {entityName || "data"}</h3>
            <p className="text-red-700 dark:text-red-300 text-sm mb-4">
              There was an error loading the table. Please refresh the page.
            </p>
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/40"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Page
            </Button>
          </CardContent>
        </Card>
      }
      resetOnPropsChange={true}
      resetKeys={entityName ? [entityName] : undefined}
    >
      {children}
    </ErrorBoundary>
  );
}

export function MobileCardErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={<MobileErrorFallback onReset={() => window.location.reload()} />}
      resetOnPropsChange={true}
    >
      {children}
    </ErrorBoundary>
  );
}

export function MobileViewErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={<MobileErrorFallback onReset={() => window.location.reload()} />}
      resetOnPropsChange={true}
    >
      {children}
    </ErrorBoundary>
  );
}
